import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { CheckCircle2, Copy, Landmark, LockKeyhole, QrCode, RefreshCw, ShieldAlert, Wallet } from "lucide-react";
import { currencies, formatCurrency, type Currency } from "@/lib/pricing";
import { useStore } from "@/lib/store-context";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Container, meta } from "@/components/store/page-parts";
import { toast } from "sonner";

export const Route = createFileRoute("/checkout")({
  head: () => meta("Secure Checkout — betteraccess.store", "Enter your billing details, choose a payment gateway and review your total."),
  component: Checkout,
});

const isCurrency = (value: string): value is Currency => (currencies as readonly string[]).includes(value);

function Checkout() {
  const { cart, clear, gateways, currency, products, user, support } = useStore();
  const navigate = useNavigate();
  const active = useMemo(() => gateways.filter(g => g.enabled), [gateways]);
  const [method, setMethod] = useState(active[0]?.id ?? "bkash-nagad");
  const [agree, setAgree] = useState(true);
  const [busy, setBusy] = useState(false);

  // Binance Modal state
  const [binanceModal, setBinanceModal] = useState(false);
  const [binanceOrderId, setBinanceOrderId] = useState("");
  const [binanceTxid, setBinanceTxid] = useState("");
  const [verifyingTx, setVerifyingTx] = useState(false);

  const selected = active.find(g => g.id === method) ?? active[0];
  const settle: Currency = selected && isCurrency(selected.settleCurrency) ? selected.settleCurrency : "BDT";

  const total = cart.reduce((sum, item) => {
    const product = products.find(p => p.slug === item.slug);
    const variant = product?.variants.find(v => v.id === item.variantId) ?? product?.variants[0];
    return sum + (variant?.price ?? 0) * item.quantity;
  }, 0);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!agree || !selected) {
      toast.error("Please agree to the terms to proceed");
      return;
    }

    const formData = new FormData(event.currentTarget);
    const email = (formData.get("email") as string)?.trim();
    const phone = (formData.get("phone") as string)?.trim() || "";
    const firstName = (formData.get("firstName") as string)?.trim() || "";
    const lastName = (formData.get("lastName") as string)?.trim() || "";
    const customerName = `${firstName} ${lastName}`.trim() || email;

    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address for digital delivery");
      return;
    }

    const primaryItem = cart[0];
    const product = products.find(p => p.slug === primaryItem.slug) ?? products[0];

    // Map UI gateway ID to backend payment gateway type
    let backendGateway = "primefollows";
    if (selected.id.includes("binance") || selected.name.toLowerCase().includes("binance")) {
      backendGateway = "binance";
    } else if (selected.id.includes("crypto") || selected.name.toLowerCase().includes("cryptomus")) {
      backendGateway = "cryptomus";
    } else if (selected.id === "wallet") {
      backendGateway = "wallet";
    }

    setBusy(true);

    try {
      const res = await fetch("/api/orders/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: product?.id || product?.slug,
          quantity: primaryItem.quantity,
          currency: settle === "USD" ? "USD" : "BDT",
          paymentGateway: backendGateway,
          customerEmail: email,
          customerPhone: phone,
          userId: user?.id || null,
        }),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || "Failed to create order");
      }

      const orderId = data.orderId || `ORD-${Date.now().toString().slice(-6)}`;

      // 1. If Wallet payment was successful
      if (data.paid) {
        clear();
        toast.success("Order paid instantly via Wallet!");
        void navigate({ to: "/order-success", search: { order: orderId } });
        return;
      }

      // 2. If PrimeFollows EPS or Cryptomus redirect URL provided
      if (data.paymentUrl) {
        clear();
        window.location.href = data.paymentUrl;
        return;
      }

      // 3. If Binance Pay
      if (backendGateway === "binance") {
        setBinanceOrderId(orderId);
        setBinanceModal(true);
        setBusy(false);
        return;
      }

      // 4. Default pending order
      clear();
      toast.success("Order created successfully!");
      void navigate({ to: "/order-success", search: { order: orderId } });
    } catch (err: any) {
      toast.error(err.message || "Checkout error. Please try again or contact support.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyBinance = async () => {
    if (!binanceTxid.trim()) {
      toast.error("Please enter the Binance Transaction ID (TXID)");
      return;
    }
    setVerifyingTx(true);
    try {
      const res = await fetch("/api/orders/verify-binance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: binanceOrderId, txid: binanceTxid.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "Verification failed");
      }
      clear();
      setBinanceModal(false);
      toast.success("Payment verified! Redirecting to order...");
      void navigate({ to: "/order-success", search: { order: binanceOrderId } });
    } catch (err: any) {
      toast.error(err.message || "Failed to verify transaction ID");
    } finally {
      setVerifyingTx(false);
    }
  };

  if (!cart.length && !binanceModal) {
    return (
      <Container className="py-24 text-center">
        <h1 className="text-3xl font-black">Nothing to check out yet</h1>
        <Button asChild className="mt-5">
          <Link to="/products">Browse products</Link>
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-8 sm:py-10">
      <div className="flex items-center gap-2 text-success">
        <LockKeyhole className="size-5" />
        <span className="text-xs font-extrabold uppercase">256-Bit SSL Encrypted Checkout</span>
      </div>
      <h1 className="mt-3 text-3xl font-black sm:text-4xl">Complete your order</h1>

      <form onSubmit={submit} className="mt-6 grid gap-6 sm:mt-8 sm:gap-8 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <Block title="Contact & Delivery Information">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" name="firstName" defaultValue={user?.name?.split(" ")[0] || ""} />
              <Field label="Last name" name="lastName" defaultValue={user?.name?.split(" ").slice(1).join(" ") || ""} />
              <Field label="Email address (Digital codes/credentials sent here)" name="email" type="email" defaultValue={user?.email || ""} wide />
              <Field label="Phone / WhatsApp (Optional for instant updates)" name="phone" type="tel" required={false} />
              <Field label="Company (Optional)" name="company" required={false} />
            </div>
          </Block>

          <Block title="Billing address">
            <p className="-mt-3 mb-4 text-xs text-muted-foreground">Standard billing details for invoice generation and payment verification.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Address line 1" name="address1" wide />
              <Field label="Address line 2 (Optional)" name="address2" required={false} wide />
              <Field label="City" name="city" />
              <Field label="State / Province" name="state" />
              <Field label="ZIP / Postal code" name="postalCode" />
              <Field label="Country / Region" name="country">
                <select
                  name="country"
                  required
                  className="mt-2 h-11 w-full rounded-md border bg-background px-3 text-sm font-normal outline-none focus:ring-2 focus:ring-ring"
                  defaultValue={countryFromCurrency(currency)}
                >
                  {countries.map(country => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Block>

          <Block title="Choose payment method">
            {active.length ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  {active.map(gateway => (
                    <button
                      type="button"
                      key={gateway.id}
                      onClick={() => setMethod(gateway.id)}
                      className={`flex min-w-0 items-center gap-2 rounded-md border p-3 text-left transition sm:gap-3 sm:p-4 ${
                        selected?.id === gateway.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/40"
                      }`}
                    >
                      <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-md bg-primary/10 text-primary">
                        {gateway.icon ? <img src={gateway.icon} alt="" className="size-9 object-contain" /> : <Landmark className="size-4" />}
                      </span>
                      <span className="min-w-0">
                        <b className="block text-sm">{gateway.name}</b>
                        <small className="text-muted-foreground">{gateway.copy}</small>
                        <small className="mt-0.5 block font-bold text-primary">Settles in {gateway.settleCurrency}</small>
                      </span>
                    </button>
                  ))}
                  {user && (
                    <button
                      type="button"
                      onClick={() => setMethod("wallet")}
                      className={`flex min-w-0 items-center gap-2 rounded-md border p-3 text-left transition sm:gap-3 sm:p-4 ${
                        method === "wallet" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/40"
                      }`}
                    >
                      <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-md bg-emerald-500/10 text-emerald-500">
                        <Wallet className="size-5" />
                      </span>
                      <span className="min-w-0">
                        <b className="block text-sm">Account Wallet</b>
                        <small className="text-muted-foreground">Balance: ৳{user.wallet_balance.toFixed(2)}</small>
                        <small className="mt-0.5 block font-bold text-emerald-500">Instant Automated Deduction</small>
                      </span>
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="rounded-md bg-secondary p-4 text-sm text-muted-foreground">
                No payment gateway is enabled yet. Enable one in the admin payments panel.
              </p>
            )}
          </Block>
        </div>

        <aside className="h-fit rounded-lg border bg-card p-5 lg:sticky lg:top-32">
          <h2 className="font-black">Order summary</h2>
          <div className="mt-4 space-y-4">
            {cart.map(item => {
              const product = products.find(p => p.slug === item.slug);
              const variant = product?.variants.find(v => v.id === item.variantId) ?? product?.variants[0];
              return product && variant ? (
                <div key={`${item.slug}-${item.variantId}`} className="flex justify-between gap-3 text-sm">
                  <span>
                    {product.name}{" "}
                    <small className="block text-muted-foreground">
                      {variant.name} × {item.quantity}
                    </small>
                  </span>
                  <b>{formatCurrency(variant.price * item.quantity, settle)}</b>
                </div>
              ) : null;
            })}
          </div>
          <div className="my-5 border-y py-4">
            <p className="flex justify-between text-lg font-black">
              <span>Payable total</span>
              <span>{formatCurrency(total, settle)}</span>
            </p>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {settle === currency
                ? "Shown in your selected display currency."
                : `Your display currency is ${currency}; this gateway settles in ${settle}.`}
            </p>
          </div>
          <label className="flex items-start gap-3 text-xs leading-5 text-muted-foreground">
            <Checkbox checked={agree} onCheckedChange={value => setAgree(value === true)} className="mt-0.5" />
            <span>
              I agree to the{" "}
              <Link to="/terms" className="font-bold text-primary">
                Terms of Service
              </Link>{" "}
              and refund policy.
            </span>
          </label>
          <Button
            type="submit"
            size="lg"
            className="mt-5 h-auto min-h-10 w-full whitespace-normal px-3 py-2 text-center font-bold"
            disabled={!agree || busy || !selected}
          >
            {busy ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="size-4 animate-spin" /> Processing order...
              </span>
            ) : selected ? (
              `Pay ${formatCurrency(total, settle)} with ${method === "wallet" ? "Wallet" : selected.name}`
            ) : (
              "Select a payment method"
            )}
          </Button>
        </aside>
      </form>

      {/* Binance Pay Verification Modal */}
      {binanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b pb-4">
              <div className="flex items-center gap-2 font-black text-amber-500">
                <QrCode className="size-5" />
                <span>Binance Pay Checkout</span>
              </div>
              <span className="rounded bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-500">{binanceOrderId}</span>
            </div>

            <div className="mt-4 space-y-4 text-sm">
              <div className="rounded-lg bg-secondary/70 p-4 text-center">
                <p className="text-xs text-muted-foreground">Payable Order Amount</p>
                <p className="text-2xl font-black text-foreground">${total.toFixed(2)} USD</p>
              </div>

              <div className="space-y-2 rounded-lg border bg-card p-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Binance Pay ID:</span>
                  <div className="flex items-center gap-1 font-mono font-bold">
                    <span>{support.binance_pay_id || "889923412"}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(support.binance_pay_id || "889923412");
                        toast.success("Pay ID copied!");
                      }}
                      className="text-primary hover:underline"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Binance UID:</span>
                  <div className="flex items-center gap-1 font-mono font-bold">
                    <span>{support.binance_pay_uid || "782390114"}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(support.binance_pay_uid || "782390114");
                        toast.success("UID copied!");
                      }}
                      className="text-primary hover:underline"
                    >
                      <Copy className="size-3" />
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-foreground">Submit Binance Transaction ID (TXID / Order ID):</label>
                <input
                  value={binanceTxid}
                  onChange={e => setBinanceTxid(e.target.value)}
                  placeholder="e.g. 2398471923847"
                  className="mt-1.5 h-11 w-full rounded-md border bg-background px-3 font-mono text-sm outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Instant automated fulfillment upon entering valid TXID.</p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setBinanceModal(false)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-amber-500 text-black hover:bg-amber-600" disabled={verifyingTx} onClick={handleVerifyBinance}>
                  {verifyingTx ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="size-4 animate-spin" /> Verifying...
                    </span>
                  ) : (
                    "Confirm Payment"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}

const countries = [
  "Bangladesh",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "India",
  "United Arab Emirates",
  "Germany",
  "France",
  "Netherlands",
  "Singapore",
  "Malaysia",
  "Other",
];
const countryFromCurrency = (currency: Currency) =>
  ({ BDT: "Bangladesh", USD: "United States", GBP: "United Kingdom", CAD: "Canada", INR: "India", AED: "United Arab Emirates", EUR: "Germany" }[
    currency
  ] ?? "United States");

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-card p-4 sm:p-5">
      <h2 className="mb-5 text-lg font-black">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  name,
  type = "text",
  defaultValue = "",
  wide = false,
  required = true,
  children,
}: {
  label: string;
  name: string;
  type?: string;
  defaultValue?: string;
  wide?: boolean;
  required?: boolean;
  children?: ReactNode;
}) {
  return (
    <label className={`text-xs font-bold ${wide ? "sm:col-span-2" : ""}`}>
      {label}
      {children ?? (
        <input
          name={name}
          type={type}
          defaultValue={defaultValue}
          required={required}
          className="mt-2 h-11 w-full rounded-md border bg-background px-3 text-sm font-normal outline-none focus:ring-2 focus:ring-ring"
        />
      )}
    </label>
  );
}
